import template from "!!raw-loader!./resources/RightMenuAccordionFilter.html";
import Spec from "csi/core/Spec";
import html from "csi/html";
import locale from "csi/locale";
import { getValuesFromRuleSpecFilterMap, RuleSpecMap } from "csi/middleware/attributeRule/RuleService";
import CheckedMultiSelectDropdown from "csi/widget/CheckedMultiSelectDropdown";
import FilteringSelect from "csi/widget/FilteringSelect";
import _TemplatedMixin from "dijit/_TemplatedMixin";
import _WidgetBase from "dijit/_WidgetBase";
import _WidgetsInTemplateMixin from "dijit/_WidgetsInTemplateMixin";
import Memory from "dojo/store/Memory";
import declare from "dojo/_base/declare";

const RightMenuAccordionFilter = declare([_WidgetBase, _TemplatedMixin, _WidgetsInTemplateMixin], {
	templateString: template,

	/** @property {RightMenuAccordionItem} accordionItem - parent widget */
	accordionItem: null,

	/** @property {DOMNode} filterAttributeFilteringSelect - filtering select for filter attributes container node  */
	filterAttributeFilteringSelect: null,

	/** @property {_WidgetBase} filteringSelect - filtering select for filter attributes widget  */
	filteringSelect: null,

	/** @property {RuleSpecMap} filterMap - map for filter */
	filterMap: null,

	/** @property {DOMNode} filterValueDropDownNode - filter value dropdown  */
	filterValueDropDownNode: null,

	/** @property {CheckedMultiSelectDropdown} multiSelect - multi select menu  */
	multiSelect: null,

	/**
	 * Click handler for Cancel button to reset dropdowns and hide filter panel.
	 */
	_onClickCancel() {
		this.resetFilterPanel();
		this.hideFilterPanel();
	},

	/**
	 * Reset dropdowns panel.
	 */
	resetFilterPanel() {
		const {
			filteringSelect,
			multiSelect,
		} = this;
		filteringSelect.set("item", null);
		multiSelect.resetSelected([]);
		multiSelect.set("store", new Memory({ data: [] }));
	},

	/**
	 * Set filter with new map.
	 */
	setFilterMap(newFilterMap: RuleSpecMap) {
		this.filterMap = { ...newFilterMap };
	},

	/**
	 * Remove keys from filter.
	 */
	removeKeysFromFilterMap(removeKeys: string[]) {
		removeKeys.forEach((key) => {
			delete this.filterMap[key];
		});
	},

	/**
	 * Hide filter panel.
	 */
	hideFilterPanel() {
		const { accordionItem } = this;
		accordionItem.filterAttributesNode.classList.add(html.CSS_HIDE);
		accordionItem.toggleFilterButtonNode.set("checked", false);
	},

	/**
	 * Click handler for Apply button in this widget to apply filter.
	 */
	onClickApply(e: Event, dontSelectLastFilterPill: boolean) {
		const { accordionItem, multiSelect, filteringSelect } = this;
		// if no values are selected (or "None" option)
		if (!multiSelect.getSelectedOption().length && this.filterMap) {
			delete this.filterMap[filteringSelect.get("value")];
		}
		accordionItem.applyFilterCallback(this);
		accordionItem.accordionAdHocFilter.renderFilters(!dontSelectLastFilterPill);
	},

	/**
	 * Select filter attribute from filterAttributeDropDownNode.
	 */
	selectFilterAttribute(spec: Spec) {
		const {
			accordionItem,
			filteringSelect,
			filterMap,
			multiSelect,
		} = this;

		accordionItem.accordionAdHocFilter.selectFilter(spec);

		const specExtendedKey = spec.key.toString();
		const selectedItem = filteringSelect.store.get(specExtendedKey);
		// when clicking on a filter pill, this item will be different, so sync it
		if (selectedItem !== filteringSelect.item) {
			filteringSelect.set("item", selectedItem);
		}

		// the store should be based on the placeholder store
		const store = accordionItem.placeholderStore.getValuesStore(spec);
		const values = getValuesFromRuleSpecFilterMap(spec, filterMap);

		multiSelect.set("spec", spec);
		multiSelect.set("store", store);
		multiSelect.resetSelected(values);
	},

	/**
	 * On change filtering select.
	 */
	onChangeFilteringSelect(id: string) {
		// when resetting the dropdown, id is ""
		if (id) {
			const storeEntry = this.filteringSelect.store.get(id);
			this.selectFilterAttribute(storeEntry.spec);
		}
	},

	/**
	 * @override
	 * Populate widget.
	 */
	postCreate() {
		const {
			accordionItem,
			filterAttributeFilteringSelect,
			filterValueDropDownNode,
		} = this;

		// filter attribute dropdown
		const filterAttributeData = [];
		for (const spec of accordionItem.filterSpecs) {
			filterAttributeData.push({
				id: spec.key.toString(),
				name: spec.name,
				spec,
			});
		}
		const filterAttributeStore = new Memory({
			data: filterAttributeData,
		});
		this.filteringSelect = new FilteringSelect({
			placeHolder: locale.resource("SelectAttribute"),
			required: false,
			store: filterAttributeStore,
			onChange: this.onChangeFilteringSelect.bind(this),
		}).placeAt(filterAttributeFilteringSelect);

		// value dropdown
		this.multiSelect = new CheckedMultiSelectDropdown({
			defaultDisplayValue: locale.resource("SelectValues"),
			store: new Memory({ data: [] }),
		});
		const { multiSelect } = this;
		filterValueDropDownNode.addChild(multiSelect);

		this.inherited(arguments);
	},
});

export = RightMenuAccordionFilter;